/**
 * Error handling utilities for classifying and extracting information from errors.
 */

/**
 * Safely get a nested value from an object using a dot-separated path.
 * Similar to lodash.get but lightweight.
 *
 * @example
 * getPath({ a: { b: { c: 1 } } }, 'a.b.c') // 1
 * getPath({ a: { b: { c: 1 } } }, 'a.x.y') // undefined
 */
const getPath = (obj: unknown, path: string): unknown => {
  if (typeof obj !== 'object' || obj === null) {
    return undefined;
  }

  const segments = path.split('.');
  let current: unknown = obj;

  for (const segment of segments) {
    if (typeof current !== 'object' || current === null) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[segment];
  }

  return current;
};

/**
 * Get the first string value found from a list of paths.
 */
const getFirstString = (
  obj: unknown,
  paths: readonly string[],
): string | undefined => {
  for (const path of paths) {
    const value = getPath(obj, path);
    if (typeof value === 'string' && value) {
      return value;
    }
  }
  return undefined;
};

/**
 * Get the first number value found from a list of paths.
 */
const getFirstNumber = (
  obj: unknown,
  paths: readonly string[],
): number | undefined => {
  for (const path of paths) {
    const value = getPath(obj, path);
    if (typeof value === 'number') {
      return value;
    }
  }
  return undefined;
};

// ============================================================================
// Shared Error Message Constants
// ============================================================================

// ============================================================================
// Path Constants for Error Field Extraction
// These define the various locations where error information can be found
// across different error shapes (hey-api, axios, fetch, etc.)
// ============================================================================

/**
 * Paths to check for URL/path information (in priority order).
 * Includes API response path fields and request URL fields.
 */
const URL_PATHS = [
  'path', // Direct root path (some API responses)
  'body.path', // hey-api style API response path
  'response.data.path', // axios style API response path
  'response.url', // fetch Response.url
  'request.url', // hey-api request
  'config.url', // axios config
  'url', // direct url property
] as const;

/**
 * Paths to check for HTTP method.
 */
const METHOD_PATHS = [
  'response.method',
  'request.method',
  'config.method',
  'method',
] as const;

/**
 * Paths to check for HTTP status code.
 */
const STATUS_PATHS = [
  'status',
  'response.status',
  'statusCode',
  'response.statusCode',
] as const;

/**
 * Paths to check for error message.
 */
const MESSAGE_PATHS = [
  'response.data.message',
  'body.message',
  'response.data.error',
  'body.error',
  'response.statusText',
  'statusText',
  'message',
] as const;

/**
 * Paths to check for error code (to prefix message).
 */
const CODE_PATHS = ['response.data.code', 'body.code', 'code'] as const;

/*s const;

/**
 * Check if an error should be skipped for toast/error handling purposes.
 *
 * Returns true for:
 * - Request aborts (AbortError, CanceledError)
 * - Network offline errors (TypeError with "Failed to fetch")
 */
export const isSkippableError = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) {
    return false;
  }

  const errorObj = err as Record<string, unknown>;

  // Abort errors
  if (errorObj.name === 'AbortError' || errorObj.name === 'CanceledError') {
    return true;
  }

  // Network offline - TypeError with specific message
  if (
    errorObj.name === 'TypeError' &&
    typeof errorObj.message === 'string' &&
    errorObj.message.includes('Failed to fetch')
  ) {
    return true;
  }

  // Check for missing response (network error)
  if (
    errorObj.name === 'TypeError' &&
    !('response' in errorObj) &&
    !('status' in errorObj)
  ) {
    return true;
  }

  return false;
};

/**
 * Extract a human-readable request name from an error object.
 *
 * Uses path-based extraction to find URL/path information from various
 * error shapes (hey-api, axios, fetch, etc.).
 *
 * Returns the full pathname like "GET /reports/training/plans".
 */
export const extractRequestName = (err: unknown): string | undefined => {
  const url = getFirstString(err, URL_PATHS);
  const method = getFirstString(err, METHOD_PATHS);

  if (!url) {
    return undefined;
  }

  // Parse URL to get just the pathname (strips query params, host, etc.)
  try {
    const parsedUrl = new URL(url, 'http://localhost');
    const pathname = parsedUrl.pathname;
    const methodPrefix = method ? `${method.toUpperCase()} ` : '';
    return `${methodPrefix}${pathname}`;
  } catch {
    // If URL parsing fails, just use the raw url
    const methodPrefix = method ? `${method.toUpperCase()} ` : '';
    return `${methodPrefix}${url}`;
  }
};

/**
 * Extract the HTTP status code from various error shapes.
 */
export const extractStatusCode = (err: unknown): number | undefined =>
  getFirstNumber(err, STATUS_PATHS);

/**
 * Extract the best available error message from various error shapes.
 *
 * Uses path-based extraction with priority order defined in MESSAGE_PATHS.
 * Prepends error code if available.
 */
export const extractErrorMessage = (
  err: unknown,
  fallback = 'Unable to perform action, no reason provided.',
): string => {
  const message = getFirstString(err, MESSAGE_PATHS);

  if (!message) {
    return fallback;
  }

  // Check for error code to prefix the message
  const code = getFirstString(err, CODE_PATHS);
  return code ? `[${code}] ${message}` : message;
};

/**
 * Extract detailed error information for developer logging (not shown to users).
 */
export const extractDevDetails = (err: unknown): Record<string, unknown> => {
  if (typeof err !== 'object' || err === null) {
    return { error: String(err) };
  }

  const errorObj = err as Record<string, unknown>;
  const details: Record<string, unknown> = {};

  // Basic error info
  if (errorObj.name) details.name = errorObj.name;
  if (errorObj.message) details.message = errorObj.message;
  if (errorObj.status) details.status = errorObj.status;
  if (errorObj.statusText) details.statusText = errorObj.statusText;
  if (errorObj.path) details.path = errorObj.path;

  // Okta-specific
  if (errorObj.errorCode) details.errorCode = errorObj.errorCode;
  if (errorObj.errorSummary) details.errorSummary = errorObj.errorSummary;

  // Response data
  if (typeof errorObj.response === 'object' && errorObj.response !== null) {
    const response = errorObj.response as Record<string, unknown>;
    details.responseStatus = response.status;
    details.responseStatusText = response.statusText;
    if (response.data) details.responseData = response.data;
    if (response.url) details.url = response.url;
  }

  // Body (hey-api style)
  if (errorObj.body) details.body = errorObj.body;

  // Stack trace (truncated for logging)
  if (typeof errorObj.stack === 'string') {
    details.stack = errorObj.stack.split('\n').slice(0, 5).join('\n');
  }

  return details;
};
